// src/pages/ReaderPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import InlineEbookSpread from "../components/InlineEbookSpread"; 
import {
  api,
  fetchJsonWithProxies,
  pdfUrlOf,
  fetchExternalPdfBuffer,
  isExternalPdf
} from "../lib/omekaClient";
import { pickLang } from "../lib/i18nPick";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import {
  Lock,
  ChevronLeft,
  ChevronRight,
  Menu 
} from "lucide-react";

// ⚙️ CONFIG: กำหนดชื่อฟิลด์จาก Omeka S ที่นี่
const PROP_ITEM_JSON = "dcterms:hasPart";
const PROP_MEDIA_TRANS = "dcterms:alternative";
const PROP_MEDIA_TLATE = "dcterms:isVersionOf";

function isPalmLeafLike(item) {
  const collect = (prop) =>
    (Array.isArray(item?.[prop]) ? item[prop] : [])
      .map((v) => (v?.["@value"] || v)?.toString().toLowerCase());

  const hay = [
    ...collect("dcterms:type"),
    ...collect("dcterms:format"),
    ...collect("schema:genre"),
    (item?.["o:resource_class_label"] || "").toString().toLowerCase(),
    (item?.["o:resource_template_label"] || "").toString().toLowerCase(),
  ].join(" | ");

  return /ใบลาน|palm[-\s]?leaf|lontar|scroll|pothi|ใบลานคร/.test(hay);
}

export default function ReaderPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [imagePages, setImagePages] = useState([]);
  const [title, setTitle] = useState(t("common.loading", "กำลังโหลด…"));
  const [itemData, setItemData] = useState(null);

  // --- State สำหรับระบบคำแปล และ เสียง ---
  const [pageTexts, setPageTexts] = useState([]); 
  const [audiobookUrl, setAudiobookUrl] = useState(null); // เพิ่ม State เก็บ URL ไฟล์เสียง
  const [activeTextTab, setActiveTextTab] = useState("transliteration");
  const [showTextPanel, setShowTextPanel] = useState(false);

  // --- UI State ---
  const [showSettings, setShowSettings] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [preferredStack, setPreferredStack] = useState("horizontal");

  const viewerRef = useRef(null);
  const [state, setState] = useState({
    page: 1,
    numPages: 0,
    dual: false,
    fitMode: "fitH",
    scale: 1,
    stackDirection: "horizontal",
  });

  const [vh, setVh] = useState(() =>
    typeof window !== "undefined" ? window.visualViewport?.height || window.innerHeight : 0
  );

  useEffect(() => {
    const recalc = () => setVh(window.visualViewport?.height || window.innerHeight);
    window.addEventListener("resize", recalc);
    window.visualViewport?.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("resize", recalc);
      window.visualViewport?.removeEventListener("resize", recalc);
    };
  }, []);

  const HEADER_H = 56;
  const contentHeight = Math.max(200, vh - HEADER_H);

  // การป้องกันการคลิกขวา/ปุ่มลัด
  useEffect(() => {
    const handleKeyDown = (e) => {
      const k = e.key?.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      if (e.keyCode === 123 || (ctrl && shift && (k === 'i' || k === 'j' || k === 'c')) || (ctrl && (k === 'u' || k === 's' || k === 'p'))) {
        e.preventDefault(); return false;
      }
    };
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("contextmenu", handleContextMenu, true);
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("contextmenu", handleContextMenu, true);
      document.body.style.userSelect = "auto";
      document.body.style.webkitUserSelect = "auto";
      document.body.style.overflow = "auto";
    };
  }, []);

  // โหลดข้อมูล
  useEffect(() => {
    let isMounted = true;
    let blobCleanup = null;

    const fetchData = async () => {
      setLoading(true); setErr(""); setPdfBlobUrl(null); setImagePages([]); setAudiobookUrl(null);
      try {
        const item = await fetchJsonWithProxies(api(`/items/${id}`));
        if (!isMounted) return;
        if (!item) throw new Error(t("errors.loadFailed", "ไม่พบข้อมูลเอกสาร"));

        setItemData(item);
        const ti = pickLang(item?.["dcterms:title"], i18n.language) || item?.["o:title"] || t("common.untitled", "Untitled");
        setTitle(ti);

        const palm = isPalmLeafLike(item);
        const stackDir = palm ? "vertical" : "horizontal";
        setPreferredStack(stackDir);
        setState(prev => ({ ...prev, stackDirection: stackDir, dual: !palm }));

        let textsData = [];
        const itemJsonText = item[PROP_ITEM_JSON]?.[0]?.["@value"];
        if (itemJsonText) {
          try {
            let parsed = JSON.parse(itemJsonText);
            
            const currentLang = i18n.language || "th"; 
            
            if (parsed && !Array.isArray(parsed)) {
                if (parsed[currentLang]) {
                    parsed = parsed[currentLang]; 
                } else if (parsed["th"]) {
                    parsed = parsed["th"]; 
                } else {
                    parsed = []; 
                }
            }

            if (Array.isArray(parsed)) {
              const maxP = Math.max(...parsed.map(p => p.page || 0), 0);
              textsData = new Array(maxP).fill({ transliteration: "", translation: "" });
              parsed.forEach(p => {
                if (p.page > 0) textsData[p.page - 1] = { transliteration: p.transliteration || "", translation: p.translation || "" };
              });
            }
          } catch (e) { console.warn("JSON Parse Error:", e); }
        }

        // โหลด Media List เพื่อหาคำแปล (ถ้าไม่มีใน JSON) และ หาไฟล์เสียง (Audio)
        const mediaList = await fetchJsonWithProxies(api(`/media?item_id=${id}`));
        
        if (Array.isArray(mediaList)) {
          // 1. จัดการคำแปล
          if (textsData.length === 0) {
            textsData = mediaList.map(m => ({
              transliteration: m[PROP_MEDIA_TRANS]?.[0]?.["@value"] || "",
              translation: m[PROP_MEDIA_TLATE]?.[0]?.["@value"] || ""
            }));
          }

          // 2. ค้นหาไฟล์เสียงหนังสือ (Audiobook)
          const targetAudio = mediaList.find(m => {
            const type = m["o:media_type"];
            const desc = m["dcterms:description"]?.[0]?.["@value"] || "";
            // ค้นหาไฟล์เสียงที่มีคำอธิบายว่า เต็ม, Full ก่อน เพื่อแยกระหว่างไฟล์แนะนำ กับ ไฟล์เต็ม
            return type?.startsWith("audio/") && (desc.includes("Full") || desc.includes("เต็ม"));
          }) || mediaList.find(m => m["o:media_type"]?.startsWith("audio/")); // ถ้าไม่เจอให้ดึงไฟล์เสียงอันแรก

          if (targetAudio && targetAudio["o:original_url"]) {
            setAudiobookUrl(targetAudio["o:original_url"]);
          }
        }
        
        setPageTexts(textsData);

        const rawPdfUrl = await pdfUrlOf(item);
        if (rawPdfUrl) {
          if (isExternalPdf(rawPdfUrl)) {
            const buffer = await fetchExternalPdfBuffer(rawPdfUrl);
            if (buffer) {
              const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
              blobCleanup = URL.createObjectURL(blob);
              if (isMounted) setPdfBlobUrl(blobCleanup);
            } else { if (isMounted) setPdfBlobUrl(rawPdfUrl); }
          } else {
            const resp = await axios.get(rawPdfUrl, { responseType: 'blob', timeout: 60000 });
            blobCleanup = URL.createObjectURL(resp.data);
            if (isMounted) setPdfBlobUrl(blobCleanup);
          }
        } else {
          const medias = Array.isArray(item?.["o:media"]) ? item["o:media"] : [];
          const imgs = [];
          for (const m of medias) {
            const md = await fetchJsonWithProxies(api(`/media/${m["o:id"]}`));
            if (md?.["o:original_url"]) imgs.push(md["o:original_url"]);
          }
          if (imgs.length > 0 && isMounted) setImagePages(imgs);
          else throw new Error(t("errors.loadFailed", "ไม่พบไฟล์เอกสาร"));
        }
      } catch (e) { if (isMounted) setErr(e.message || "Error"); }
      finally { if (isMounted) setLoading(false); }
    };

    fetchData();
    return () => { isMounted = false; if (blobCleanup) URL.revokeObjectURL(blobCleanup); };
  }, [id, i18n.language, t]); 

  const onClose = () => { if (location.key !== "default") navigate(-1); else navigate("/"); };

  const hasContent = !!pdfBlobUrl || imagePages.length > 0;

  return (
    <div className="fixed inset-0 z-[9999] bg-[#fcfaf8]" style={{ height: vh }}>
      <Helmet><title>{title} · Digital Reader</title></Helmet>

      {/* Header (Top Bar) */}
      <div className="absolute left-0 right-0 top-0 h-[56px] flex items-center justify-between bg-white border-b border-[#e5d7ca] px-2 md:px-4 shadow-sm z-50">
        
        {/* ปุ่มปิด */}
        <button onClick={onClose} className="flex items-center justify-center bg-gray-50 hover:bg-[#f1e6da] text-[#7b6c61] hover:text-[#d8653b] px-3 md:px-4 py-1.5 rounded-full transition-colors border border-gray-200 shadow-sm ml-1 shrink-0">
          <span className="text-sm font-bold hidden md:inline">{t("reader.closeWindow", "ปิดหน้าต่าง")}</span>
          <span className="text-sm font-bold md:hidden">{t("reader.close", "ปิด")}</span>
        </button>

        {/* ชื่อเรื่อง */}
        <div className="flex-1 truncate px-2 md:px-6 text-center flex items-center justify-center gap-2">
          <Lock size={14} className="text-[#9a8a7e] hidden sm:block shrink-0" />
          <h1 className="text-sm md:text-base font-bold text-[#3d342e] italic truncate">{title}</h1>
        </div>

        {/* เมนูมุมขวาบน */}
        <div className="flex items-center gap-1.5 md:gap-2 relative pr-1 shrink-0">
          
          {/* บทแปล */}
          <button 
            onClick={() => setShowTextPanel(!showTextPanel)} 
            className={`flex items-center px-3 md:px-4 py-1.5 rounded-full transition-colors border shadow-sm ${showTextPanel ? 'bg-[#d8653b] text-white border-[#d8653b]' : 'bg-gray-50 hover:bg-[#f1e6da] text-[#7b6c61] border-gray-200'}`}
          >
            <span className="text-sm font-bold">{t("reader.translation", "บทแปล")}</span>
          </button>

          {/* เมนูตั้งค่า / Hamburger */}
          <div className="relative">
            <button 
              onClick={() => setShowSettings(!showSettings)} 
              className={`flex items-center justify-center px-2.5 py-1.5 md:px-4 md:py-1.5 rounded-full transition-colors border shadow-sm ${showSettings ? 'bg-[#d8653b] text-white border-[#d8653b]' : 'bg-gray-50 hover:bg-[#f1e6da] text-[#7b6c61] border-gray-200'}`}
            >
              <Menu size={20} className="block md:hidden" />
              <span className="hidden md:inline text-sm font-bold">{t("reader.settings", "ตั้งค่า")}</span>
            </button>
            
            {/* Dropdown ตั้งค่า */}
            {showSettings && (
              <div className="absolute top-10 md:top-12 right-0 z-[60] bg-white p-4 md:p-5 rounded-2xl shadow-2xl border border-[#e5d7ca] w-64 md:w-72 max-h-[80vh] overflow-y-auto">
                
                <h4 className="text-sm font-bold text-[#d8653b] mb-4">{t("reader.pageSettings.title", "ตั้งค่าการแสดงผล")}</h4>
                
                {/* เค้าโครงหน้า */}
                <div className="mb-4">
                  <span className="text-xs text-gray-500 font-bold mb-2 block">{t("reader.pageSettings.displayFormat", "รูปแบบการแสดงผล")}</span>
                  <div className="flex bg-[#fcfaf8] rounded-lg p-1 border border-[#e5d7ca] gap-1">
                    <button className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${!state.dual ? 'bg-white shadow-sm text-[#d8653b] border border-gray-100' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => viewerRef.current?.toggleDual?.()}>{t("reader.pageSettings.singlePage", "หน้าเดี่ยว")}</button>
                    <button className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${state.dual ? 'bg-white shadow-sm text-[#d8653b] border border-gray-100' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => viewerRef.current?.toggleDual?.()}>{t("reader.pageSettings.doublePage", "หน้าคู่")}</button>
                  </div>
                </div>

                {/* การจัดขนาด */}
                <div className="mb-4">
                  <span className="text-xs text-gray-500 font-bold mb-2 block">{t("reader.pageSettings.fitScreen", "การจัดพอดีจอ")}</span>
                  <div className="flex bg-[#fcfaf8] rounded-lg p-1 border border-[#e5d7ca] gap-1">
                    <button className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${state.fitMode === 'fitW' ? 'bg-white shadow-sm text-[#d8653b] border border-gray-100' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => viewerRef.current?.fitWidth?.()}>{t("reader.pageSettings.fitWidth", "พอดีความกว้าง")}</button>
                    <button className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${state.fitMode === 'fitH' ? 'bg-white shadow-sm text-[#d8653b] border border-gray-100' : 'text-gray-500 hover:bg-gray-50'}`} onClick={() => viewerRef.current?.fitHeight?.()}>{t("reader.pageSettings.fitHeight", "พอดีความสูง")}</button>
                  </div>
                </div>

                {/* ซูม */}
                <div className="mb-6">
                  <span className="text-xs text-gray-500 font-bold mb-2 block">{t("reader.pageSettings.zoom", "ซูม")}</span>
                  <div className="flex bg-[#fcfaf8] rounded-lg p-1 border border-[#e5d7ca] items-center justify-between px-2 gap-2">
                    <button onClick={() => viewerRef.current?.zoomOut?.()} className="flex-1 py-2 bg-white border border-gray-100 rounded text-sm font-bold text-gray-600 hover:text-[#d8653b] shadow-sm">{t("reader.pageSettings.zoomOut", "ย่อ")}</button>
                    <span className="text-sm font-bold text-[#7b6c61] min-w-[40px] text-center">{Math.round(state.scale * 100)}%</span>
                    <button onClick={() => viewerRef.current?.zoomIn?.()} className="flex-1 py-2 bg-white border border-gray-100 rounded text-sm font-bold text-gray-600 hover:text-[#d8653b] shadow-sm">{t("reader.pageSettings.zoomIn", "ขยาย")}</button>
                  </div>
                </div>

                {/* ความสว่าง/คมชัด */}
                <div className="space-y-4">
                  {['brightness', 'contrast'].map(type => (
                    <div key={type}>
                      <label className="text-xs text-gray-500 font-bold flex justify-between mb-1">{type === 'brightness' ? t("reader.pageSettings.brightness", "ความสว่าง") : t("reader.pageSettings.contrast", "ความคมชัด")} <span>{type === 'brightness' ? brightness : contrast}%</span></label>
                      <input type="range" min="50" max="150" value={type === 'brightness' ? brightness : contrast} onChange={(e) => type === 'brightness' ? setBrightness(e.target.value) : setContrast(e.target.value)} className="w-full accent-[#d8653b]" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Viewer Area */}
      <div className="absolute left-0 right-0 overflow-hidden bg-[#f4f4f4]" style={{ top: HEADER_H, bottom: 0 }}>
        
        {/* ปุ่มเปลี่ยนหน้า ซ้าย-ขวา */}
        {hasContent && !loading && !err && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-30 flex items-center justify-start px-2 group pointer-events-none">
              <button 
                onClick={() => viewerRef.current?.prev?.()}
                className="w-12 h-24 md:w-16 md:h-32 bg-white/40 hover:bg-white/90 backdrop-blur-sm border border-white/50 text-[#7b6c61] hover:text-[#d8653b] rounded-2xl flex items-center justify-center shadow-lg transition-all opacity-50 group-hover:opacity-100 pointer-events-auto"
              >
                <ChevronLeft size={36} />
              </button>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-30 flex items-center justify-end px-2 group pointer-events-none">
              <button 
                onClick={() => viewerRef.current?.next?.()}
                className="w-12 h-24 md:w-16 md:h-32 bg-white/40 hover:bg-white/90 backdrop-blur-sm border border-white/50 text-[#7b6c61] hover:text-[#d8653b] rounded-2xl flex items-center justify-center shadow-lg transition-all opacity-50 group-hover:opacity-100 pointer-events-auto"
              >
                <ChevronRight size={36} />
              </button>
            </div>

            {/* เลขหน้า */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
              <div className="bg-[#3d342e]/80 backdrop-blur-md text-white px-6 py-2.5 rounded-full shadow-lg border border-white/10 text-base font-mono font-bold tracking-widest">
                <span className="text-[#f1e6da]">{state.page}</span> <span className="opacity-50">/</span> <span className="opacity-80">{state.numPages}</span>
              </div>
            </div>
          </>
        )}

        <div className="w-full h-full" style={{ filter: `brightness(${brightness}%) contrast(${contrast}%)` }}>
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-[#9a8a7e] gap-4">
              <div className="w-8 h-8 border-4 border-[#d8653b] border-t-transparent rounded-full animate-spin"></div>
              <div className="animate-pulse font-bold text-base">{t("reader.viewer.preparing", "กำลังเตรียมเอกสาร...")}</div>
            </div>
          )}
          {!loading && err && (
            <div className="h-full flex flex-col items-center justify-center text-[#9a8a7e] gap-4 p-8 text-center">
              <div className="text-base font-bold text-red-500">{err}</div>
            </div>
          )}
          {!loading && !err && hasContent && (
            <InlineEbookSpread
              ref={viewerRef}
              fileUrl={pdfBlobUrl || undefined}
              imagePages={imagePages}
              viewportHeight={contentHeight}
              onStateChange={setState}
              initialFit="fitH" 
              stackDirection={preferredStack}
              pageGap={32}
              disableSwipeNav 
            />
          )}
        </div>

        {/* กล่องคำแปล */}
        {showTextPanel && !loading && !err && hasContent && (
          <div className="absolute left-4 right-4 md:left-12 md:right-12 top-4 bottom-20 z-40 flex flex-col pointer-events-none">
            <div className="bg-white/95 backdrop-blur-md shadow-2xl border border-[#e5d7ca] rounded-2xl flex flex-col h-full overflow-hidden pointer-events-auto transition-all">
              
              {/* Header กล่องแปล */}
              <div className="px-4 md:px-6 py-3 border-b border-[#e5d7ca] bg-[#f1e6da]/40 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-base md:text-lg font-bold text-[#3d342e]">
                    {t("reader.translationPanel.title", "คำปริวรรต / คำแปล")}
                  </h3>
                  <span className="text-xs md:text-sm font-bold bg-[#d8653b] text-white px-3 py-1 rounded-full shadow-inner">
                    {t("reader.translationPanel.allPages", "เนื้อหาทั้งหมด")}
                  </span>
                </div>

                {/* --- ส่วนที่เพิ่มเข้ามา: เครื่องเล่นไฟล์เสียง --- */}
                {audiobookUrl && (
                  <div className="flex-1 flex justify-center px-2">
                    <audio 
                      src={audiobookUrl} 
                      controls 
                      autoPlay 
                      controlsList="nodownload"
                      className="h-8 w-full max-w-[200px] md:max-w-[300px] outline-none rounded-full"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
                {/* ------------------------------------------- */}
                
                {/* ปุ่มสลับภาษา และ ปุ่มปิดกล่อง */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <div className="flex bg-[#f1e6da] p-1 rounded-lg gap-1 flex-1 md:flex-none">
                    <button onClick={() => setActiveTextTab('transliteration')} className={`flex-1 md:flex-none px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTextTab === 'transliteration' ? 'bg-white text-[#d8653b] shadow-sm' : 'text-[#7b6c61] hover:bg-white/50'}`}>{t("reader.translationPanel.transliterationTab", "คำปริวรรต")}</button>
                    <button onClick={() => setActiveTextTab('translation')} className={`flex-1 md:flex-none px-4 py-2 text-sm font-bold rounded-md transition-all ${activeTextTab === 'translation' ? 'bg-white text-[#d8653b] shadow-sm' : 'text-[#7b6c61] hover:bg-white/50'}`}>{t("reader.translationPanel.translationTab", "คำแปล")}</button>
                  </div>
                  <button onClick={() => setShowTextPanel(false)} className="px-5 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-bold transition-colors whitespace-nowrap">
                    {t("reader.translationPanel.closeButton", "ปิด")}
                  </button>
                </div>
              </div>

              {/* พื้นที่เนื้อหาคำแปลแบบ Continuous Scroll */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-[#fcfaf8]/50">
                {pageTexts && pageTexts.length > 0 ? (
                  pageTexts.map((data, index) => {
                    const content = activeTextTab === 'transliteration' ? data.transliteration : data.translation;
                    
                    if (!content) return null;

                    return (
                      <div key={index} className="mb-8 border-b border-[#e5d7ca] pb-6 last:border-0 last:mb-0 last:pb-0">
                        <div className="text-sm font-bold text-[#9a8a7e] mb-4">
                          {t("reader.translationPanel.pageSingle", { page: index + 1, defaultValue: `หน้า ${index + 1}` })}
                        </div>
                        <div className="whitespace-pre-line text-[16px] md:text-[18px] leading-relaxed text-[#3d342e] font-serif">
                          {content}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-[#9a8a7e] mt-8 text-sm italic">
                    {t("reader.translationPanel.noData", "ไม่มีข้อมูลสำหรับแสดงผล")}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}